<template>
  <div class="dashboard" :class="{ 'tv-mode': isTV }">
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
    </div>
    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
      <button @click="fetchData" class="retry-button">Retry</button>
    </div>
    <div v-else class="dashboard-content">
      <div class="utilization-overlay" v-if="trafficData.length">
        <div class="utilization-circle">
            <div
                class="utilization-progress"
                :class="{ 'is-pulsing': shouldPulse }"
                :style="{
                background: `conic-gradient(
                  hsl(${120 - (utilization * 1.2)}, 80%, 50%) ${utilization * 3.6}deg,
                  #3a3a3a ${utilization * 3.6}deg
                )`
              }"
            >
            <div class="utilization-inner">
              <span class="utilization-text">{{ utilization }}%</span>
            </div>
          </div>
        </div>
      </div>
      <div class="chart-container">
        <canvas ref="trafficChart"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick, computed } from 'vue';
import Chart from 'chart.js/auto';
import { useTrafficStore } from '@/stores/trafficStore';
import { recordVisitor } from '@/stores/visitorTracker';

export default {
  name: 'Dashboard',
  setup() {
    const trafficStore = useTrafficStore();
    const trafficData = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const trafficChart = ref(null);
    let chart = null;

    const timeFormat = new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    const isTV = ref(false);

    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      isTV.value = params.get('mode') === 'tv';
      console.log("Running on TV ");
    }

    const fontScale = isTV.value ? 3 : 1;
    const shouldPulse = ref(false);

    const allTimeActive = 3; // magic number determined after 3 days of monitoring
    const removeStationary = (num) => {
      return Math.max(num - allTimeActive, 0);
    };

    const fetchData = async () => {
      loading.value = true;
      error.value = null;
      try {
        const response = await fetch('/api/traffic/today');
        if (!response.ok) throw new Error('Unable to load traffic data');
        const data = await response.json();
        data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        trafficData.value = data;

        await nextTick(); // wait for <canvas> to be in DOM
      } catch (err) {
        error.value = err.message;
      } finally {
        loading.value = false;
      }
    };

    const createChart = () => {
      const labels = [];
      const actual = [];
      const measured = [];
      const now = Date.now();
      const isMobile = window.innerWidth < 768;

      // 24h or 5h cutoff
      const cutoffHours = isMobile ? 3 : 24;
      const cutoffTime = now - cutoffHours * 60 * 60 * 1000;

      // filter data if mobile
      const relevantData = isMobile
          ? trafficData.value.filter(item => new Date(item.timestamp).getTime() >= cutoffTime)
          : trafficData.value;

      relevantData.forEach(item => {
        const date = new Date(item.timestamp);
        const timeLabel = timeFormat.format(date);
        labels.push(timeLabel);
        actual.push(removeStationary(item.estimated_real_amount));
        measured.push(item.unique_device_count);
      });


      Chart.register({
        id: 'pulsingDot',
        afterDatasetsDraw(chart) {
          const ctx = chart.ctx;
          const datasetIndex = 0; // "Actual"
          const meta = chart.getDatasetMeta(datasetIndex);
          if (!meta || !meta.data.length) return;

          const dataset = chart.data.datasets[datasetIndex];
          const lastPoint = meta.data[meta.data.length - 1];
          const { x, y } = lastPoint.getProps(['x', 'y'], true);

          const pulse = Math.abs(Math.sin(Date.now() / 900)) * 3; // slower, subtler pulse
          const radius = 5 + pulse;

          ctx.save();
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, 2 * Math.PI);
          ctx.fillStyle = dataset.borderColor; // match graph color
          ctx.globalAlpha = 0.5 + Math.sin(Date.now() / 1000) * 0.1; // organic fade
          ctx.fill();
          ctx.restore();
        },
      });


      let ds = [
        {
          label: 'Actual',
          data: actual,
          backgroundColor: 'rgba(54, 162, 235, 0.3)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true,
          pointRadius: ctx => ctx.dataIndex === ctx.chart.data.labels.length - 1 ? 4 : 0,
          pointHoverRadius: 5,
        },
        {
          label: 'Measured',
          data: measured,
          backgroundColor: 'rgba(54,235,105,0.3)',
          borderColor: 'rgb(54,235,117)',
          borderWidth: 2,
          tension: 0.4,
          fill: true,
          pointRadius: ctx => ctx.dataIndex === ctx.chart.data.labels.length - 1 ? 4 : 0,
          pointHoverRadius: 5,
          hidden: true,
        },
        {
          label: 'Predicted',
          data: Array(labels.length).fill(null),
          backgroundColor: 'rgba(255, 99, 132, 0.3)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1.5,
          borderDash: [3, 3],
          tension: 0.4,
          fill: true,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
      ]

      if (isTV.value)  {  //do not show measurements in isTv
        ds.splice(1, 1);
      }

      chart = new Chart(trafficChart.value, {
        type: 'line',
        data: {
          labels,
          datasets: ds,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              align: 'end',
              labels: {
                boxWidth: 10 * fontScale,
                usePointStyle: true,
                padding: 10 * fontScale,
                font: {
                  size: 9 * fontScale,
                  family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                  weight: '600',
                },
                color: '#D1D5DB',
              },
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: ctx => {
                  const descriptions = {
                    Measured: "MAC addresses detected in the area.",
                    Actual: "Estimated visitor count from measured data (regression model).",
                    Predicted: "Forecasted visitors (in development).",
                  };
                  return `${ctx.dataset.label}: ${ctx.formattedValue} — ${descriptions[ctx.dataset.label] || ''}`;
                },
              },
              titleFont: {
                size: 10 * fontScale,
                weight: '600',
                family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              },
              bodyFont: {
                size: 9 * fontScale,
                family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              },
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              titleColor: '#d1d5db',
              bodyColor: '#e5e7eb',
              borderColor: '#38bdf8',
              borderWidth: 1 * fontScale,
              padding: 8 * fontScale,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(141,188,5,0.15)',
              },
              ticks: {
                font: {
                  size: 9 * fontScale,
                  family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                },
                color: '#d1d5db',
              },
            },
            x: {
              grid: { display: false },
              ticks: {
                maxRotation: 0,
                padding: 5 * fontScale,
                autoSkip: true,
                maxTicksLimit: 6,
                font: {
                  size: 7 * fontScale,
                  family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                },
                color: '#d1d5db',
              },
            },
          },
        },
      });
    };

    watch(() => trafficStore.trafficData, (newTrafficData) => {
      if (!chart || newTrafficData.length === 0) return;

      const existingIds = new Set(trafficData.value.map(item => item.id));
      const newItems = newTrafficData.filter(item => !existingIds.has(item.id));
      if (newItems.length === 0) return;

      newItems.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      newItems.forEach(item => {
        const date = new Date(item.timestamp);
        const timeLabel = timeFormat.format(date);
        chart.data.labels.push(timeLabel);
        chart.data.datasets[0].data.push(removeStationary(item.estimated_real_amount));
        chart.data.datasets[1].data.push(item.unique_device_count);
        chart.data.datasets[2].data.push(null); // predictions später
        trafficData.value.push(item);
      });

      chart.update();
    }, { deep: true });

    const utilization = computed(() => {
      if (!trafficData.value.length) return 0;
      const latest = removeStationary(trafficData.value.at(-1)?.estimated_real_amount) ?? 0;

      // avoid rounding noise and negative values
      const ratio = Math.max(latest, 0) / 264;
      const percent = Math.floor(ratio * 100 + 1e-6);

      return Math.min(percent, 100);
    });


    watch(utilization, (newVal, oldVal) => {
      if (Math.round(newVal) !== Math.round(oldVal)) {
        shouldPulse.value = false;

        // force DOM reflow so next toggle re-triggers animation
        requestAnimationFrame(() => {
          shouldPulse.value = true;
        });
      }
    });

    const animateDot = () => {
      if (!chart) return;
      chart.update('none'); // 'none' = no layout recalculation, just redraw
      requestAnimationFrame(animateDot);
    };
    requestAnimationFrame(animateDot);

    onMounted(async () => {
      await fetchData(); // fetch & set data
      await nextTick();  // ensure canvas is in DOM

      if (trafficChart.value?.getContext && !chart) {
        createChart();
      }

      recordVisitor();
      trafficStore.setupWebSocket();
    });

    return {
      trafficData,
      loading,
      error,
      trafficChart,
      fetchData,
      utilization,
      shouldPulse,
      isTV,
    };
  },
};
</script>

<style scoped>
.dashboard {
  padding: 0;
  max-width: 100%;
  margin: 0 auto;
  height: 95vh;
  display: flex;
  flex-direction: column;
  color: #e0e0e0; /* light text */
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  color: #e0e0e0;
}

.spinner {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #8dbc05; /* corporate green */
  animation: spin 1s infinite linear;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  color: #f87171; /* softer red for error, readable on dark */
}

.retry-button {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #8dbc05;
  color: #121212;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.3s ease;
}

.retry-button:hover {
  background-color: #81a905;
}

.dashboard-content {
  margin: 20px;
  display: flex;
  background-color: #2a2a2a; /* dark container */
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  flex-direction: column;
  flex: 1;
  color: #e0e0e0;
}

.chart-container {
  background-color: #2a2a2a; /* match dashboard bg */
  padding: 0.5rem;
  box-shadow: none;
  height: 100%;
  width: auto;
  position: relative;
  flex: 1;
  color: #e0e0e0;
}

.utilization-overlay {
  position: absolute;
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}


.utilization-circle {
  width: 75px;
  height: 75px;
  display: flex;
  justify-content: center;
  align-items: center;
}


.utilization-progress {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background 0.5s ease;
  will-change: transform, box-shadow, background;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-transform: translateZ(0); /* hardware accel hint for Safari */
}

.utilization-progress::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: 0 0 0 0 rgba(141, 188, 5, 0.3);
  pointer-events: none;
}

.utilization-progress.is-pulsing::after {
  animation: pulse 5s ease-in-out 1; /* one calm pulse */
}

.utilization-inner {
  width: 85%;
  height: 85%;
  background-color: #2a2a2a;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.utilization-text {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: #e0e0e0;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(141, 188, 5, 0.3);
  }
  50% {
    box-shadow: 0 0 15px 5px rgba(141, 188, 5, 0.25);
  }
}

/* === Mobile adjustments === */
@media (max-width: 768px) {
  .chart-container {
    padding: 0.25rem;
  }
  .utilization-circle {
    width: 50px;
    height: 50px;
  }
  .utilization-text {
    font-size: 0.9rem;
  }
  .dashboard-content {
    margin: 10px;
    padding: 10px;
  }
}


/* === TV Mode === */
.dashboard.tv-mode {
  font-size: 1.25rem;
}

.dashboard.tv-mode .utilization-text {
  font-size: 3rem;
  font-weight: 700;
}

.dashboard.tv-mode .utilization-circle {
  width: 120px;
  height: 120px;
}

  .dashboard.tv-mode .chart-container {
  padding: 1rem;
}

.dashboard.tv-mode .dashboard-content {
  padding: 30px;
}

.dashboard.tv-mode .dashboard-content,
.dashboard.tv-mode .chart-container {
  border-radius: 16px;
}

</style>
